import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { observer } from 'mobx-react';
import TimeSlotLabel from './TimeSlotLabel';
import useStore from '../../helpers/useStore';
import { CalendarContext } from '../../stores/Calendar/calendar.provider';

interface StickyTimeSlotLabelProps {
  calendarRootRef: React.RefObject<HTMLDivElement>;
}

/**
 * A sticky/floating TimeSlotLabel that appears vertically on the left side when scrolling horizontally.
 * It remains fixed on the left edge of the viewport when the time column scrolls out of view.
 * Observes CalendarStore for highlightedTimeSlot, isKeyboardNavigation, and originalDragTime.
 * Only responds to keyboard navigation, not mouse movement.
 */
const StickyTimeSlotLabel: React.FC<StickyTimeSlotLabelProps> = observer(({
  calendarRootRef,
}) => {
  // Get state from CalendarStore (mobx observable)
  const CalendarStore = useStore(CalendarContext);
  const { highlightedTimeSlot, isKeyboardNavigation, originalDragTime, patientDrawerOpen } = CalendarStore;
  const [position, setPosition] = useState<{ top: number } | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Drawer width for positioning
  const DRAWER_WIDTH = 320; // Same as in Scheduler
  const drawerWidth = patientDrawerOpen ? DRAWER_WIDTH : 0;

  // Track horizontal scroll position
  useEffect(() => {
    const root = calendarRootRef.current;
    if (!root) return;

    // Find the horizontal scroll container (prefer fc-scroller elements)
    const scrollContainer = root.querySelector('.fc-scroller') as HTMLElement;
    if (!scrollContainer) return;
  }, [calendarRootRef]);

  // Update position and visibility based on highlighted time slot (keyboard navigation only)
  useEffect(() => {
    const root = calendarRootRef.current;
    if (!root || !highlightedTimeSlot || !isKeyboardNavigation) {
      setIsVisible(false);
      return;
    }

    // Find a cell with the highlighted time to get its position
    const cell = root.querySelector(`.fc-timegrid-slot-resource[data-time="${highlightedTimeSlot}"]`) as HTMLElement;
    if (!cell) {
      setIsVisible(false);
      return;
    }

    const cellRect = cell.getBoundingClientRect();
    setIsVisible(true);
    setPosition({
      top: cellRect.top,
    });
  }, [calendarRootRef, highlightedTimeSlot, isKeyboardNavigation]);

  if (!isVisible || !position || !highlightedTimeSlot) {
    return null;
  }

  // Parse time for TimeSlotLabel props
  const [hours, minutes] = highlightedTimeSlot.split(':').map(Number);
  const timeString = highlightedTimeSlot;
  const isHighlighted = true; // Always highlight in sticky mode
  const isOriginalDragTime = originalDragTime === timeString;

  // Calculate left position: base offset + drawer width
  const leftPosition = 60 + drawerWidth;

  // Render to document body for proper fixed positioning on the left side
  return ReactDOM.createPortal(
    <div
      id="sticky-time-slot-label-test"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${leftPosition}px`,
        zIndex: 10,
        backgroundColor: 'rgba(156, 26, 53, 0.95)',
        color: 'white',
        boxShadow: '2px 2px 12px rgba(0, 0, 0, 0.4)',
        borderRight: '3px solid #9C1A35',
        borderRadius: '4px',
        pointerEvents: 'none',
        transition: 'all 0.2s ease-out',
        fontWeight: 'bold',
        minWidth: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '1',
        paddingTop: '1px',
        paddingBottom: '1px',
      }}
    >
      <TimeSlotLabel
        hour={hours}
        minute={minutes}
        isHighlighted={isHighlighted}
        highlightedTimeSlot={highlightedTimeSlot}
        isOriginalDragTime={isOriginalDragTime}
        originalDragTime={originalDragTime}
      />
    </div>,
    document.body
  );
});

export default StickyTimeSlotLabel;
